import { create } from 'zustand';

import type { VerificationStatus } from '@/features/email-verification/types';
import { buildDirectBackendUrl, createDirectBackendHeaders } from '@/lib/direct-backend-api';
import { fetchAuthorStats } from '@/lib/author-stats-api';

interface ImageData {
  id: number;
  alt: string;
  caption: string | null;
  updatedAt: string;
  createdAt: string;
  url: string;
  thumbnailURL: string;
  filename: string;
  mimeType: string;
  filesize: number;
  width: number;
  height: number;
  focalX: number;
  focalY: number;
  sizes: {
    thumbnail: {
      url: string;
      width: number;
      height: number;
      mimeType: string;
      filesize: number;
      filename: string;
    };
    square: {
      url: string;
      width: number;
      height: number;
      mimeType: string;
      filesize: number;
      filename: string;
    };
    small: {
      url: string;
      width: number;
      height: number;
      mimeType: string;
      filesize: number;
      filename: string;
    };
    medium: {
      url: string | null;
      width: number | null;
      height: number | null;
      mimeType: string | null;
      filesize: number | null;
      filename: string | null;
    };
    large: {
      url: string | null;
      width: number | null;
      height: number | null;
      mimeType: string | null;
      filesize: number | null;
      filename: string | null;
    };
    xlarge: {
      url: string | null;
      width: number | null;
      height: number | null;
      mimeType: string | null;
      filesize: number | null;
      filename: string | null;
    };
    og: {
      url: string;
      width: number;
      height: number;
      mimeType: string;
      filesize: number;
      filename: string;
    };
  };
}

export interface UserData {
  id: string | number;
  name: string;
  username?: string;
  email: string;
  bio?: string;
  instagramId?: string | null;
  vkId?: string | null;
  telegramId?: string | null;
  maxId?: string | null;
  ref?: string;
  avatar?: ImageData;
  banner?: ImageData;
  fanficsCount?: number;
  commentsCount?: number;
  collectionsFanficsCount?: number;
  privateFanficsCount?: number;
  likesCount?: number;
  isAdult?: boolean;
  verificationStatus?: VerificationStatus | null;
  pendingEmail?: string | null;
  codeSentAt?: string | null;
  resendAvailableAt?: string | null;
  donation?: {
    donationUserId?: string | null;
    hasCard: boolean;
  };
}

interface ProfileState {
  userData: UserData | null;
  isLoading: boolean;
  isLoadingUser: boolean;
  error: string | null;
  lastFetchTime: number | null;
  isFetching: boolean;

  // Actions
  setUserData: (userData: UserData | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setIsLoadingUser: (isLoadingUser: boolean) => void;
  setError: (error: string | null) => void;
  fetchUserData: (
    accessToken: string,
    userId: string | number,
    options?: { force?: boolean },
  ) => Promise<void>;
  updateProfile: (
    data: {
      name: string;
      username: string;
      email?: string;
      bio?: string;
      instagramId?: string | null;
      vkId?: string | null;
      telegramId?: string | null;
      maxId?: string | null;
      donation?: {
        donationUserId?: string | null;
        hasCard: boolean;
      };
    },
    accessToken: string,
    userId: string | number,
  ) => Promise<boolean>;
  clearProfile: () => void;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

export const useProfileStore = create<ProfileState>((set, get) => ({
  userData: null,
  isLoading: false,
  isLoadingUser: false,
  error: null,
  lastFetchTime: null,
  isFetching: false,

  setUserData: (userData) => set({ userData }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setIsLoadingUser: (isLoadingUser) => set({ isLoadingUser }),
  setError: (error) => set({ error }),

  fetchUserData: async (accessToken: string, userId: string | number, options) => {
    const state = get();

    // Check if data is fresh (within cache duration)
    if (
      !options?.force &&
      state.userData &&
      state.lastFetchTime &&
      Date.now() - state.lastFetchTime < CACHE_DURATION
    ) {
      return;
    }

    // Prevent multiple simultaneous fetches
    if (state.isFetching) {
      return;
    }

    set({ isLoadingUser: !state.userData, isFetching: true, error: null });

    try {
      const defaultUserData: UserData = {
        id: userId,
        name: '',
        username: '',
        email: '',
        bio: '',
        avatar: undefined,
      };

      const response = await fetch(buildDirectBackendUrl('/api/users/me'), {
        method: 'GET',
        headers: createDirectBackendHeaders({
          accessToken,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const data = await response.json();
      const userDataFromResponse = data.user || data || defaultUserData;

      // Fetch author stats for correct fanfics count
      try {
        const stats = await fetchAuthorStats({ accessToken });
        if (stats.total !== undefined) {
          userDataFromResponse.fanficsCount = stats.total;
        }
      } catch (statsError) {
        console.error('Failed to fetch fanfics stats:', statsError);
        // Keep the value from profile API if stats fetch fails
      }

      set({
        userData: userDataFromResponse,
        lastFetchTime: Date.now(),
        isLoadingUser: false,
        isFetching: false,
        error: null,
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
      set({
        error: error instanceof Error ? error.message : 'Unknown error',
        isLoadingUser: false,
        isFetching: false,
      });
      throw error;
    }
  },

  updateProfile: async (data, accessToken, userId) => {
    const state = get();
    set({ isLoading: true, error: null });

    try {
      const updatedUserData: UserData = {
        ...state.userData!,
        name: data.name,
        username: data.username,
        bio: data.bio,
        instagramId: data.instagramId,
        vkId: data.vkId,
        telegramId: data.telegramId,
        maxId: data.maxId,
        ...(data.email !== undefined ? { email: data.email } : {}),
        donation: {
          donationUserId: data.donation?.donationUserId,
          hasCard: data.donation?.hasCard ?? false,
        },
      };

      const response = await fetch(buildDirectBackendUrl('/api/users/profile'), {
        method: 'PATCH',
        headers: createDirectBackendHeaders({
          accessToken,
          hasJsonContentType: true,
        }),
        body: JSON.stringify({
          userId,
          name: data.name,
          username: data.username,
          bio: data.bio,
          instagramId: data.instagramId,
          vkId: data.vkId,
          telegramId: data.telegramId,
          maxId: data.maxId,
          ...(data.email !== undefined ? { email: data.email } : {}),
          donation: {
            donationUserId: data.donation?.donationUserId,
            hasCard: data.donation?.hasCard ?? false,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const updatedResponse = await response.json();
      const nextUserData = updatedResponse.user || updatedUserData;

      set({
        userData: nextUserData,
        lastFetchTime: Date.now(),
        isLoading: false,
        error: null,
      });

      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      set({
        error: error instanceof Error ? error.message : 'Unknown error',
        isLoading: false,
      });
      return false;
    }
  },

  clearProfile: () =>
    set({
      userData: null,
      isLoading: false,
      isLoadingUser: false,
      error: null,
      lastFetchTime: null,
      isFetching: false,
    }),
}));
